import { Suspense, lazy } from "react";
import {
  Alert,
  Anchor,
  Code,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Link, useParams } from "react-router";
import {
  useBlockHead,
  useWalletBalanceSeries,
  useWalletMiningEfficiency,
  useWalletMiningSeries,
  useWalletOwnedDomains,
  useWalletSentencesPage,
  type WalletOwnedDomain,
  type WalletSentenceEntry,
} from "../api";
import { isValidScriptPubKey, shortenHex } from "../identity";
import { formatCog } from "../units";
import { QueryCard } from "../components/QueryCard";
import { OutcomeBadge } from "../components/OutcomeBadge";
import { EfficiencyGrid } from "../components/EfficiencyGrid";
import { PaginationControls } from "../components/PaginationControls";
import { usePaginationCursor } from "../hooks/usePaginationCursor";
import { SENTENCES_PAGE_SIZE } from "../constants";

const MiningSeriesChart = lazy(() =>
  import("../charts/MiningSeriesChart").then((m) => ({ default: m.MiningSeriesChart })),
);
const BalanceSeriesChart = lazy(() =>
  import("../charts/BalanceSeriesChart").then((m) => ({ default: m.BalanceSeriesChart })),
);

export function PerWallet() {
  const { scriptPubKey: rawSpk } = useParams();
  const spk = isValidScriptPubKey(rawSpk) ? rawSpk : null;

  if (spk === null) {
    return (
      <Stack gap="md">
        <Title order={2}>Wallet</Title>
        <Alert color="red" title="Invalid scriptPubKey">
          The URL must look like <Code>/wallet/&lt;lowercase-hex&gt;</Code> with an even
          number of hex digits.
        </Alert>
      </Stack>
    );
  }

  return <PerWalletView scriptPubKey={spk} />;
}

interface PerWalletViewProps {
  readonly scriptPubKey: string;
}

function PerWalletView({ scriptPubKey }: PerWalletViewProps) {
  const head = useBlockHead();
  const toHeight = head.data ? Number(head.data.height) : null;

  const series = useWalletMiningSeries(scriptPubKey, toHeight);
  const efficiency = useWalletMiningEfficiency(scriptPubKey, toHeight);
  const balance = useWalletBalanceSeries(scriptPubKey, toHeight);
  const ownedDomains = useWalletOwnedDomains(scriptPubKey, toHeight);

  const pagination = usePaginationCursor();
  const sentences = useWalletSentencesPage(scriptPubKey, toHeight, {
    cursor: pagination.cursor,
    limit: SENTENCES_PAGE_SIZE,
  });

  return (
    <Stack gap="lg">
      <Group gap="xs" align="baseline">
        <Title order={2}>Wallet</Title>
        <Code fz="md">{shortenHex(scriptPubKey, 12, 8)}</Code>
      </Group>

      <QueryCard title="Mining activity" query={series} alsoWatch={[head]}>
        {(data) =>
          data.entries.length > 0 ? (
            <Suspense fallback={<Loader size="sm" />}>
              <MiningSeriesChart entries={data.entries} />
            </Suspense>
          ) : (
            <Text c="dimmed">No submissions yet.</Text>
          )
        }
      </QueryCard>

      <QueryCard title="Mining efficiency" query={efficiency}>
        {(data) => <EfficiencyGrid totals={data} />}
      </QueryCard>

      <QueryCard title="COG balance trajectory" query={balance}>
        {(data) =>
          data.entries.length > 0 ? (
            <Suspense fallback={<Loader size="sm" />}>
              <BalanceSeriesChart entries={data.entries} />
            </Suspense>
          ) : (
            <Text c="dimmed">No balance changes in this window.</Text>
          )
        }
      </QueryCard>

      <QueryCard title="Owned domains" query={ownedDomains}>
        {(data) => <OwnedDomainsList rows={data.entries} />}
      </QueryCard>

      <QueryCard
        title={
          <Group justify="space-between" align="baseline">
            <Title order={4}>Submitted sentences</Title>
            <PaginationControls
              pagination={pagination}
              isFetching={sentences.isFetching}
              nextCursor={sentences.data?.nextCursor ?? null}
            />
          </Group>
        }
        query={sentences}
      >
        {(data) => <SentencesTable rows={data.entries} />}
      </QueryCard>
    </Stack>
  );
}

interface OwnedDomainsListProps {
  readonly rows: readonly WalletOwnedDomain[];
}

function OwnedDomainsList({ rows }: OwnedDomainsListProps) {
  if (rows.length === 0) {
    return (
      <Text c="dimmed">
        This wallet hasn&apos;t registered any domains. Domain ownership is fixed at
        registration; mining rewards may be paid to wallets other than the registrant.
      </Text>
    );
  }
  return (
    <Table withTableBorder striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Domain</Table.Th>
          <Table.Th>Registered at height</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr key={row.domainId}>
            <Table.Td>
              <Anchor
                component={Link}
                to={`/domain/${row.domainId.toString()}`}
                underline="hover"
              >
                <Group gap={6} wrap="nowrap">
                  <Text>{row.name}</Text>
                  <Text size="xs" c="dimmed">
                    #{row.domainId}
                  </Text>
                </Group>
              </Anchor>
            </Table.Td>
            <Table.Td>{row.registeredAtHeight.toString()}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

interface SentencesTableProps {
  readonly rows: readonly WalletSentenceEntry[];
}

function SentencesTable({ rows }: SentencesTableProps) {
  if (rows.length === 0) {
    return <Text c="dimmed">No sentences in this window.</Text>;
  }
  return (
    <Table withTableBorder striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Height</Table.Th>
          <Table.Th>Domain</Table.Th>
          <Table.Th>Outcome</Table.Th>
          <Table.Th>Sentence</Table.Th>
          <Table.Th>Reward</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr key={`${row.txid}:${row.txIndex.toString()}`}>
            <Table.Td>{row.height.toString()}</Table.Td>
            <Table.Td>
              <Anchor
                component={Link}
                to={`/domain/${row.miningDomainId.toString()}`}
                underline="hover"
                size="sm"
              >
                #{row.miningDomainId}
              </Anchor>
            </Table.Td>
            <Table.Td>
              <OutcomeBadge entry={row} />
            </Table.Td>
            <Table.Td>
              {row.sentenceText !== null ? (
                <Text size="sm">{row.sentenceText}</Text>
              ) : (
                <Text size="xs" c="dimmed" ff="monospace">
                  {shortenHex(row.rawSentenceBytes, 12, 6)}
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              {row.rewardCogtoshi > 0n
                ? formatCog(row.rewardCogtoshi, {
                    groupIntegerDigits: true,
                    minDecimals: 2,
                  })
                : "—"}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

